import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message, Tag } from 'antd-v5';
import PageHeaderLayout from '@/component/page-header-layout';
import { Button, Card, Modal, Table } from '@/components/index';
import { deleteAdminRoleRequest, getAdminRoleListRequest } from '@/state/request/adminUser';
import './style.less';

const ROLE_LIST_PATH = '/system/roleManagement';
const isActionDisabled = (value) => value === false;

const getPageFromSearch = (search) => {
  const page = Number(new URLSearchParams(search || '').get('page'));
  return Number.isInteger(page) && page > 0 ? page : 1;
};

const getPageSearch = (page) => `?page=${page}`;

const SystemRoleManagement = ({ history, location }) => {
  const [modal, contextHolder] = Modal.useModal();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [currentPage, setCurrentPage] = useState(() => getPageFromSearch(location?.search));

  const getRoleList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminRoleListRequest();
      if (res?.code === 200) {
        setDataSource(Array.isArray(res.data) ? res.data : []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getRoleList();
  }, [getRoleList]);

  useEffect(() => {
    setCurrentPage(getPageFromSearch(location?.search));
  }, [location?.search]);

  const handlePageChange = (pagination) => {
    const nextPage = Number(pagination.current) || 1;
    setCurrentPage(nextPage);
    history.replace({
      pathname: ROLE_LIST_PATH,
      search: getPageSearch(nextPage),
    });
  };

  const handleDelete = (record) => {
    if (isActionDisabled(record.enableDelete)) return;
    modal.confirm({
      className: 'ui-modal',
      title: '提示',
      icon: null,
      content: '确定删除该角色吗？',
      okText: '删除',
      okType: 'danger',
      onOk: async () => {
        const res = await deleteAdminRoleRequest({ roleId: record.roleId });
        if (res?.code === 200) {
          message.success('删除成功');
          getRoleList();
        }
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        title: '角色名称',
        dataIndex: 'remark',
        key: 'remark',
        render: (remark, record) => (
          <span>
            {remark || '--'}
            {record.systemAdmin && (
              <Tag color='success' className='system-role-preset-tag'>
                预设
              </Tag>
            )}
          </span>
        ),
      },
      {
        title: '操作',
        key: 'action',
        width: 180,
        render: (_, record) => {
          const editDisabled = isActionDisabled(record.enableEdit);
          const deleteDisabled = isActionDisabled(record.enableDelete);

          return (
            <div className='system-role-actions'>
              <button
                type='button'
                disabled={editDisabled}
                onClick={() => {
                  if (!editDisabled) {
                    history.push({
                      pathname: `${ROLE_LIST_PATH}/edit/${record.roleId}`,
                      search: getPageSearch(currentPage),
                    });
                  }
                }}
              >
                编辑
              </button>
              <button
                type='button'
                disabled={deleteDisabled}
                onClick={() => handleDelete(record)}
              >
                删除
              </button>
            </div>
          );
        },
      },
    ],
    [currentPage, history, modal],
  );

  return (
    <PageHeaderLayout>
      <div className='system-role-management'>
        <Card bordered={false}>
          <div className='system-role-toolbar'>
            <Button type='primary' onClick={() => history.push(`${ROLE_LIST_PATH}/add`)}>
              新增
            </Button>
          </div>
          <Table
            rowKey='roleId'
            loading={loading}
            columns={columns}
            dataSource={dataSource}
            pagination={{ current: currentPage }}
            onChange={handlePageChange}
          />
        </Card>
      </div>
      {contextHolder}
    </PageHeaderLayout>
  );
};

export default SystemRoleManagement;
